import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bookKey, getBook, updateBook } from "../api";
import { useT } from "../i18n";
import MutationError from "../components/MutationError";

/**
 * FreeToggle offers a published book to people without an account (ADR-0070).
 *
 * One switch, two effects, deliberately: it decides what the public gallery
 * shows a visitor *and* whether a visitor may open the book at all, by link or
 * otherwise. Two switches would be two things nobody could tell apart, and a
 * switch that only filtered the gallery would be a setting that lies the moment
 * somebody shares a link.
 *
 * It takes effect at once. A book's edition freezes what a reader is served
 * (ADR-0062); this says who may be served, so it is not part of one and never
 * waits for a publish.
 */
export default function FreeToggle({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });

  const set = useMutation({
    mutationFn: (free: boolean) => updateBook(bookID, { free }),
    onSuccess: (updated) => {
      client.setQueryData(bookKey(bookID, { draft: true }), updated);
      void client.invalidateQueries({ queryKey: ["books"] });
    },
  });

  const free = book.data?.free ?? false;
  // Nothing to offer until it is published: a draft has no readers at all, so
  // a switch here would promise something publishing has not done yet.
  const published = book.data?.published ?? false;
  // Free means something only on a book everyone may read (ADR-0070, ADR-0152);
  // a private book keeps its setting for when it is made public again.
  const isPublic = published && book.data?.visibility === "public";

  return (
    <div data-testid="free-panel" data-free={free ? "true" : "false"}>
      <h2 className="text-sm font-medium">{t.free.title}</h2>
      <p className="mt-1 text-xs text-muted">{t.free.note}</p>

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          data-testid="toggle-free"
          disabled={!book.data || set.isPending || !isPublic}
          onClick={() => set.mutate(!free)}
          className={`rounded-control border px-3 py-1.5 text-sm transition-colors disabled:opacity-40 ${
            free ? "border-sage bg-sage-soft text-ink" : "border-line hover:bg-sunk"
          }`}
        >
          {set.isPending ? t.free.working : free ? t.free.turnOff : t.free.turnOn}
        </button>
        <span className="text-xs text-muted">{free ? t.free.on : t.free.off}</span>
      </div>

      {!published && <p className="mt-2 text-xs text-muted">{t.free.publishFirst}</p>}
      {published && !isPublic && (
        <p data-testid="free-public-first" className="mt-2 text-xs text-muted">
          {t.free.publicFirst}
        </p>
      )}
      <MutationError error={set.error} fallback={t.free.failed} />
    </div>
  );
}
